import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface ApiConfig {
  baseUrl: string;
  citiesEndpoint: string;
  timeout: number;
  retryAttempts: number;
}

export interface AuthConfig {
  tokenKey: string;
  tokenExpiration: number;
  refreshTokenKey: string;
}

export interface StorageConfig {
  userLocationKey: string;
  userPreferencesKey: string;
  prefix: string;
}

export interface FeatureFlags {
  enableAnalytics: boolean;
  enablePushNotifications: boolean;
  enableOfflineMode: boolean;
  enableDebugMode: boolean;
}

export interface UiConfig {
  defaultLanguage: string;
  theme: string;
  itemsPerPage: number;
  maxRetries: number;
}

export interface ExternalConfig {
  googleMapsApiKey: string;
  analyticsId: string;
  sentryDsn: string;
}

export interface AppEnvironment {
  production: boolean;
  version: string;
  appName: string;
  api: ApiConfig;
  auth: AuthConfig;
  storage: StorageConfig;
  features: FeatureFlags;
  ui: UiConfig;
  external: ExternalConfig;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly config: AppEnvironment = environment;

  /**
   * Get the complete configuration object
   */
  getConfig(): AppEnvironment {
    return this.config;
  }

  /**
   * Check if the app is running in production mode
   */
  isProduction(): boolean {
    return this.config.production;
  }

  /**
   * Get the app version
   */
  getVersion(): string {
    return this.config.version;
  }

  /**
   * Get the app name
   */
  getAppName(): string {
    return this.config.appName;
  }

  /**
   * Get API configuration
   */
  getApiConfig(): ApiConfig {
    return this.config.api;
  }

  /**
   * Get the base API URL
   */
  getApiBaseUrl(): string {
    return this.config.api.baseUrl;
  }

  /**
   * Get the cities endpoint URL
   */
  getCitiesEndpoint(): string {
    return this.config.api.citiesEndpoint;
  }

  /**
   * Get authentication configuration
   */
  getAuthConfig(): AuthConfig {
    return this.config.auth;
  }

  /**
   * Get storage configuration
   */
  getStorageConfig(): StorageConfig {
    return this.config.storage;
  }

  /**
   * Get feature flags
   */
  getFeatureFlags(): FeatureFlags {
    return this.config.features;
  }

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.config.features[feature];
  }

  /**
   * Get UI configuration
   */
  getUiConfig(): UiConfig {
    return this.config.ui;
  }

  /**
   * Get external services configuration
   */
  getExternalConfig(): ExternalConfig {
    return this.config.external;
  }

  /**
   * Get storage key with prefix
   */
  getStorageKey(key: string): string {
    return `${this.config.storage.prefix}${key}`;
  }
}
